import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInlineBanner, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { isSystemOrg } from './governmentOrgHierarchy';
export function GovernmentOrgDetailPanel({ org, ancestors, suborgs, canEdit, error, onSelect, onAddChild, onEdit, onDelete, onMerge, headerAction }) {
    const system = isSystemOrg(org);
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        className: "space-y-3 p-4",
        "data-testid": Testids.deliveryRegistries.governmentOrgDetail,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-base font-semibold",
                        children: org.name
                    }),
                    org.acronym && /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm text-muted-foreground",
                        children: [
                            "(",
                            org.acronym,
                            ")"
                        ]
                    }),
                    headerAction,
                    system ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Shared reference"
                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        "data-testid": Testids.deliveryRegistries.governmentOrgTenantAddedBadge,
                        children: "Tenant-added"
                    })
                ]
            }),
            (org.officeCode || org.locationText || org.referenceSource) && /*#__PURE__*/ _jsxs("dl", {
                className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm",
                "data-testid": Testids.deliveryRegistries.governmentOrgFacts,
                children: [
                    org.officeCode && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "text-muted-foreground",
                                children: "Office code"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: org.officeCode
                            })
                        ]
                    }),
                    org.locationText && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "text-muted-foreground",
                                children: "Location"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: org.locationText
                            })
                        ]
                    }),
                    org.referenceSource && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "text-muted-foreground",
                                children: "Reference source"
                            }),
                            /*#__PURE__*/ _jsxs("dd", {
                                "data-testid": Testids.deliveryRegistries.governmentOrgReferenceSource,
                                children: [
                                    org.referenceSource,
                                    org.referenceSourceId ? ` · ${org.referenceSourceId}` : ''
                                ]
                            })
                        ]
                    })
                ]
            }),
            system && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Shared government organization reference — read-only. Add a suborganization to extend it for your organization."
            }),
            error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: error
            }),
            ancestors.length > 0 && /*#__PURE__*/ _jsx("nav", {
                "aria-label": "Ancestors",
                className: "flex flex-wrap items-center gap-1 text-sm",
                "data-testid": Testids.deliveryRegistries.governmentOrgDetailAncestors,
                children: ancestors.map((a, i)=>/*#__PURE__*/ _jsxs("span", {
                        "data-testid": Testids.deliveryRegistries.governmentOrgDetailAncestor,
                        children: [
                            i > 0 && /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: " › "
                            }),
                            a.name
                        ]
                    }, a.id))
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Suborganizations"
                    }),
                    suborgs.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "None yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "mt-1 space-y-1",
                        "data-testid": Testids.deliveryRegistries.governmentOrgDetailChildren,
                        children: suborgs.map((c)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "link",
                                    "data-testid": Testids.deliveryRegistries.governmentOrgDetailChild,
                                    onClick: ()=>onSelect(c.id),
                                    children: c.name
                                })
                            }, c.id))
                    })
                ]
            }),
            canEdit && /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: onAddChild,
                        "data-testid": Testids.deliveryRegistries.governmentOrgAddChild,
                        children: "Add suborganization"
                    }),
                    !system && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "outline",
                                onClick: onEdit,
                                "data-testid": Testids.deliveryRegistries.governmentOrgEdit,
                                children: "Edit"
                            }),
                            onMerge && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "outline",
                                onClick: onMerge,
                                "data-testid": Testids.registryMerge.open,
                                children: "Merge into…"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "destructive",
                                onClick: onDelete,
                                "data-testid": Testids.deliveryRegistries.governmentOrgDelete,
                                children: "Delete"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
